import { useEffect, useRef, useState } from "react";
import { Axios } from "../../../Api/axios";
import { STD, ALL_CLASSES, SUBJECTS_BY_CLASS } from "../../../Api/Api";
import LoadingSubmit from "../../../Components/Loading/Loading";
import DashDialog from "../../../Components/Dashboard/DashDialog";
import DashSelect from "../../../Components/Dashboard/DashSelect";
import { useNavigate } from "react-router-dom";
import { useCounts } from "../../../context/CountsContext";
import "./add-student.css";

export default function AddStudent() {
  const [name,           setName]           = useState("");
  const [phone,          setPhone]          = useState("");
  const [vegSystem,      setVegSystem]      = useState("");
  const [grades,         setGrades]         = useState([]);
  const [selectedGradeId, setSelectedGradeId] = useState("");
  const [classLoading,   setClassLoading]   = useState(true);
  const [classError,     setClassError]     = useState(null);
  const [subjects,       setSubjects]       = useState([]);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [submitLoading,  setSubmitLoading]  = useState(false);
  const [studentCode,    setStudentCode]    = useState(null);
  const [phoneError,     setPhoneError]     = useState("");
  const [submitError,    setSubmitError]    = useState("");
  const [copied,         setCopied]         = useState(false);

  const nameRef = useRef(null);
  const nav = useNavigate();
  const { refreshCounts } = useCounts();

  useEffect(() => { nameRef.current?.focus(); }, []);

  useEffect(() => {
    Axios.get(ALL_CLASSES)
      .then((res) => setGrades(res.data.classes || res.data || []))
      .catch(() => setClassError("تعذر تحميل قائمة الصفوف"))
      .finally(() => setClassLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedGradeId || vegSystem !== "subject") {
      setSubjects([]); setSelectedSubjects([]); return;
    }
    setLoadingSubjects(true);
    Axios.get(SUBJECTS_BY_CLASS(selectedGradeId))
      .then((res) => setSubjects(res.data.subjects || []))
      .catch(() => setSubjects([]))
      .finally(() => setLoadingSubjects(false));
  }, [selectedGradeId, vegSystem]);

  const handleCopy = () => {
    navigator.clipboard.writeText(studentCode.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSubmit = async () => {
    setPhoneError(""); setSubmitError(""); setSubmitLoading(true);
    try {
      const regSystem = vegSystem === "class" ? "نظام صفي" : "نظام مواد";
      const fd = new FormData();
      fd.append("name",                name.trim());
      fd.append("phone",               phone.trim());
      fd.append("registration_system", regSystem);
      fd.append("class_id",            selectedGradeId);
      if (vegSystem === "subject" && selectedSubjects.length > 0)
        fd.append("subjectIds", JSON.stringify(selectedSubjects));
      const res = await Axios.post(STD, fd);
      setStudentCode({ code: res.data.code, expiresAt: res.data["ينتهي_في"] });
      refreshCounts();
    } catch (err) {
      const data = err.response?.data || {};
      const msg = data.error || data.message || "حدث خطأ، يرجى المحاولة مجدداً";
      if (msg.includes("رقم الجوال")) setPhoneError(msg);
      else setSubmitError(msg);
    } finally {
      setSubmitLoading(false);
    }
  };

  const isValid =
    name.trim().length > 1 &&
    phone.trim().length > 5 &&
    !!selectedGradeId &&
    !!vegSystem &&
    (vegSystem !== "subject" || selectedSubjects.length > 0);

  /* ── Success screen ── */
  if (studentCode) {
    return (
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
        <div style={{ background: "#fff", borderRadius: "20px", padding: "40px 36px", maxWidth: "420px", width: "90%", textAlign: "center", boxShadow: "0 20px 60px rgba(0,0,0,0.3)", direction: "rtl" }}>
          <div style={{ fontSize: "2.8rem", marginBottom: "14px" }}>🎉</div>
          <h2 style={{ color: "#00a74f", fontWeight: 800, marginBottom: "8px", fontSize: "1.25rem" }}>تم تسجيل الطالب بنجاح</h2>
          <p style={{ color: "#6b7280", marginBottom: "22px", fontSize: "0.92rem" }}>أعطِ الكود التالي للطالب لتفعيل حسابه</p>
          <div style={{ background: "#f0fdf4", border: "2px dashed #00a74f", borderRadius: "14px", padding: "20px 24px", marginBottom: "18px" }}>
            <p style={{ margin: "0 0 8px", fontSize: "0.82rem", color: "#6b7280" }}>كود التفعيل</p>
            <span style={{ fontSize: "2.2rem", fontWeight: 800, letterSpacing: "8px", color: "#00a74f", fontFamily: "monospace", display: "block", marginBottom: "14px" }}>{studentCode.code}</span>
            <button onClick={handleCopy} style={{ background: copied ? "#007a38" : "#00a74f", color: "#fff", border: "none", borderRadius: "8px", padding: "8px 24px", fontWeight: 700, fontSize: "0.88rem", cursor: "pointer", transition: "background 0.2s" }}>
              {copied ? "✓ تم النسخ" : "نسخ الكود"}
            </button>
          </div>
          {studentCode.expiresAt && (
            <p style={{ color: "#ef4444", fontSize: "0.83rem", marginBottom: "24px" }}>⚠️ ينتهي صلاحية الكود في: <strong>{studentCode.expiresAt}</strong></p>
          )}
          <button onClick={() => nav("/dashboard/users")} style={{ background: "linear-gradient(135deg,#00a74f,#007a38)", color: "#fff", border: "none", borderRadius: "12px", padding: "13px 32px", fontWeight: 700, fontSize: "1rem", cursor: "pointer", width: "100%", boxShadow: "0 4px 14px rgba(0,167,79,0.35)" }}>
            حسناً، تم
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {submitLoading && <LoadingSubmit />}
      <DashDialog
        title="إضافة طالب جديد"
        onSubmit={handleSubmit}
        submitDisabled={!isValid || submitLoading}
        submitLabel={submitLoading ? "جاري الإضافة..." : "إضافة الطالب"}
      >
        {submitError && <div className="as-error-banner">⚠️ {submitError}</div>}

        {/* Class */}
        <div className="as-field">
          <label className="as-label">
            <svg width="13" height="13" fill="currentColor" viewBox="0 0 24 24" className="as-label-icon">
              <path d="M12 3L1 9l11 6 9-4.91V17h2V9L12 3z"/>
            </svg>
            الصف
          </label>
          <DashSelect
            value={selectedGradeId}
            onChange={setSelectedGradeId}
            options={[
              { value: "", label: classLoading ? "جاري التحميل..." : classError ? "حدث خطأ" : "اختر الصف" },
              ...grades.map((g) => ({ value: g.id, label: g.name })),
            ]}
            icon={
              <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 3L1 9l11 6 9-4.91V17h2V9L12 3z"/>
              </svg>
            }
          />
        </div>

        {/* Name */}
        <div className="as-field">
          <label className="as-label" htmlFor="as-name">
            <svg width="13" height="13" fill="currentColor" viewBox="0 0 24 24" className="as-label-icon">
              <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"/>
            </svg>
            اسم الطالب
          </label>
          <div className="as-input-wrap">
            <input
              id="as-name"
              ref={nameRef}
              className="as-input"
              type="text"
              placeholder="الاسم الكامل..."
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
        </div>

        {/* Phone */}
        <div className="as-field">
          <label className="as-label" htmlFor="as-phone">
            <svg width="13" height="13" fill="currentColor" viewBox="0 0 24 24" className="as-label-icon">
              <path d="M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.46.57 3.58a1 1 0 0 1-.25 1.01l-2.2 2.2z"/>
            </svg>
            رقم الهاتف
          </label>
          <div className={`as-input-wrap${phoneError ? " as-error" : ""}`}>
            <input
              id="as-phone"
              className="as-input"
              type="tel"
              placeholder="مثال: 0933123456"
              value={phone}
              onChange={(e) => { setPhone(e.target.value); setPhoneError(""); }}
              required
            />
          </div>
          {phoneError && <p className="as-field-err">{phoneError}</p>}
        </div>

        {/* Registration type */}
        <div className="as-field">
          <label className="as-label">
            <svg width="13" height="13" fill="currentColor" viewBox="0 0 24 24" className="as-label-icon">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm4 18H6V4h7v5h5v11z"/>
            </svg>
            نوع التسجيل
          </label>
          <div className="as-type-row">
            <button
              type="button"
              className={`as-type-btn${vegSystem === "class" ? " active" : ""}`}
              onClick={() => setVegSystem("class")}
            >
              <svg width="22" height="22" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 3L1 9l11 6 9-4.91V17h2V9L12 3zm0 12.5L3 11.09V17c0 2.76 4.03 5 9 5s9-2.24 9-5v-5.91L12 15.5z"/>
              </svg>
              نظام صفي
              <div className="as-type-dot" />
            </button>
            <button
              type="button"
              className={`as-type-btn${vegSystem === "subject" ? " active" : ""}`}
              onClick={() => setVegSystem("subject")}
            >
              <svg width="22" height="22" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2zm-2 14H8v-2h8v2zm0-4H8v-2h8v2zm0-4H8V6h8v2z"/>
              </svg>
              نظام مواد
              <div className="as-type-dot" />
            </button>
          </div>
        </div>

        {/* Subjects — only when "subject" mode and grade is chosen */}
        {vegSystem === "subject" && selectedGradeId && (
          <div className="as-field">
            <div className="as-subjects-box">
              <div className="as-subjects-label">المواد المختارة</div>
              <div className="as-chips">
                {selectedSubjects.length === 0 ? (
                  <span className="as-chip-empty">لم يتم اختيار مواد بعد</span>
                ) : (
                  selectedSubjects.map((subId) => {
                    const sub = subjects.find((s) => s.id === subId);
                    return sub ? (
                      <button
                        key={sub.id}
                        type="button"
                        className="as-chip"
                        onClick={() => setSelectedSubjects((p) => p.filter((id) => id !== sub.id))}
                        title="اضغط لإزالة المادة"
                      >
                        {sub.name} <span className="as-chip-x">×</span>
                      </button>
                    ) : null;
                  })
                )}
              </div>

              <div className="as-subjects-label">المواد المتاحة</div>
              {loadingSubjects ? (
                <p className="as-subj-loading">جاري تحميل المواد...</p>
              ) : (
                <div className="as-available">
                  {subjects.filter((s) => !selectedSubjects.includes(s.id)).length === 0 ? (
                    <span className="as-chip-empty">لا توجد مواد متاحة</span>
                  ) : (
                    subjects
                      .filter((s) => !selectedSubjects.includes(s.id))
                      .map((sub) => (
                        <button
                          key={sub.id}
                          type="button"
                          className="as-avail-btn"
                          onClick={() => setSelectedSubjects((p) => [...p, sub.id])}
                        >
                          {sub.name}
                        </button>
                      ))
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </DashDialog>
    </>
  );
}
